import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, RotateCcw, Headset, Bot, User } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "bnm_chat_session";
const HISTORY_KEY = "bnm_chat_history";

const SUGGESTIONS = [
  "What does BNM do?",
  "What is the Carbon-Free Generator?",
  "Calculate my solar system",
  "How can I partner with BNM?",
  "Request a quotation",
  "Contact BNM",
];

const WELCOME = {
  role: "assistant",
  content:
    "Hello 👋 Welcome to BNM Sustainable Energy Limited. I'm **BNM AI Assistant**. I can help you learn about our clean-energy solutions, Carbon-Free Generator, solar systems, energy calculations, products, services, partnerships, and more. How can I help you today?",
};

function renderRich(text) {
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      const to = m[2];
      if (to.startsWith("/")) {
        parts.push(
          <Link key={k++} to={to} className="font-semibold text-emerald-700 underline decoration-emerald-300 hover:text-emerald-900">
            {m[1]}
          </Link>
        );
      } else {
        parts.push(
          <a key={k++} href={to} target="_blank" rel="noopener noreferrer" className="font-semibold text-emerald-700 underline decoration-emerald-300 hover:text-emerald-900">
            {m[1]}
          </a>
        );
      }
    } else {
      parts.push(<strong key={k++}>{m[3]}</strong>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3" data-testid="chat-typing" aria-label="BNM AI Assistant is typing">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="h-2 w-2 rounded-full bg-emerald-500"
          animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
          transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </div>
  );
}

export default function ChatWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return parsed.length ? parsed : [WELCOME];
    } catch {
      return [WELCOME];
    }
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [actions, setActions] = useState([]);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const hidden = pathname.startsWith("/admin");

  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-40)));
    } catch { /* ignore */ }
  }, [messages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 250);
  }, [open]);

  const getSession = () => {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`).replace(/-/g, "");
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setError("");
    setActions([]);
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: getSession() }),
      });
      if (!res.ok || !res.body) {
        const detail = res.status === 429 ? "You're sending messages quickly — please wait a moment." : "The assistant is temporarily unavailable.";
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let gotDelta = false;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const line = ev.trim();
          if (!line.startsWith("data:")) continue;
          let data;
          try {
            data = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }
          if (data.type === "delta") {
            gotDelta = true;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.content };
              return copy;
            });
          } else if (data.type === "done") {
            setActions(data.actions || []);
          } else if (data.type === "error") {
            throw new Error(data.detail);
          }
        }
      }
      if (!gotDelta) throw new Error("No response received. Please try again.");
    } catch (e) {
      setMessages((m) => m.slice(0, -1));
      setError(e.message || "Something went wrong. Please try again or contact the BNM team.");
    } finally {
      setBusy(false);
    }
  };

  const clearChat = () => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(HISTORY_KEY);
    setMessages([WELCOME]);
    setActions([]);
    setError("");
  };

  if (hidden) return null;

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="fixed z-[70] bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[400px] h-[min(620px,72vh)] flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
            role="dialog"
            aria-label="Chat with BNM AI Assistant"
          >
            <div className="flex items-center gap-3 bg-obsidian px-4 py-3.5">
              <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-pine text-volt">
                <Bot className="h-5 w-5" aria-hidden="true" />
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-obsidian bg-emerald-400" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm font-bold text-white leading-tight">BNM AI Assistant</p>
                <p className="text-[11px] text-slate-400 leading-tight">Official digital representative · BNM Sustainable Energy</p>
              </div>
              <button type="button" onClick={clearChat} data-testid="chat-clear" aria-label="Clear conversation"
                className="rounded-md p-2 text-slate-400 hover:text-volt hover:bg-white/5 transition-colors">
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
              </button>
              <button type="button" onClick={() => setOpen(false)} data-testid="chat-close" aria-label="Close chat"
                className="rounded-md p-2 text-slate-400 hover:text-white hover:bg-white/5 transition-colors">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto bg-mist px-3.5 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-obsidian text-volt">
                      <Bot className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                  )}
                  <div
                    data-testid={m.role === "user" ? `chat-message-user-${i}` : `chat-message-ai-${i}`}
                    className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "rounded-br-sm bg-obsidian text-white"
                        : "rounded-bl-sm border border-emerald-100 bg-white text-slate-800 shadow-sm"
                    }`}
                  >
                    {m.content ? renderRich(m.content) : (busy && i === messages.length - 1 ? <TypingDots /> : "")}
                  </div>
                  {m.role === "user" && (
                    <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                      <User className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                  )}
                </div>
              ))}

              {messages.length === 1 && !busy && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-suggestions">
                  {SUGGESTIONS.map((q, i) => (
                    <button key={q} type="button" onClick={() => send(q)} data-testid={`chat-suggestion-${i}`}
                      className="rounded-full border border-emerald-200 bg-white px-3.5 py-2 text-xs font-medium text-emerald-800 hover:border-emerald-400 hover:bg-emerald-50 transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {actions.length > 0 && !busy && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-actions">
                  {actions.map((a) => (
                    <Link key={a.to + a.label} to={a.to} data-testid={`chat-cta-${a.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="rounded-full bg-obsidian px-4 py-2 text-xs font-semibold text-white hover:bg-pine hover:text-volt transition-colors">
                      {a.label}
                    </Link>
                  ))}
                </div>
              )}

              {error && (
                <p role="alert" data-testid="chat-error" className="rounded-md border border-red-200 bg-red-50 px-3.5 py-2.5 text-xs text-red-700">
                  {error}
                </p>
              )}
            </div>

            <div className="border-t border-slate-200 bg-white px-3 pt-2.5 pb-3">
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="flex items-center gap-2"
              >
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  data-testid="chat-input"
                  placeholder="Ask about BNM, solar, the Generator…"
                  maxLength={2000}
                  className="h-11 flex-1 rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send" aria-label="Send message"
                  className="flex h-11 w-11 items-center justify-center rounded-lg bg-obsidian text-white hover:bg-pine hover:text-volt transition-colors disabled:opacity-40">
                  <Send className="h-4 w-4" aria-hidden="true" />
                </button>
              </form>
              <div className="mt-2 flex items-center justify-between">
                <Link to="/contact" data-testid="chat-contact-team"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900">
                  <Headset className="h-3.5 w-3.5" aria-hidden="true" />
                  Contact BNM Team
                </Link>
                <span className="text-[10px] text-slate-400">BNM Sustainable Energy Limited</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-launcher"
        aria-label={open ? "Close chat with BNM" : "Chat with BNM"}
        aria-expanded={open}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        className="fixed z-[70] bottom-5 right-4 sm:right-6 flex items-center gap-2.5 rounded-full bg-obsidian py-3 pl-4 pr-5 text-white shadow-[0_10px_40px_rgba(7,24,17,0.45)] border border-white/10 hover:bg-pine hover:text-volt transition-colors"
      >
        {open ? <X className="h-5 w-5" aria-hidden="true" /> : <MessageCircle className="h-5 w-5" aria-hidden="true" />}
        <span className="text-sm font-semibold">{open ? "Close" : "Chat with BNM"}</span>
        {!open && <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-400" aria-hidden="true" />}
      </motion.button>
    </>
  );
}
